/** Where users land after logging in when no deeplink was captured. */
export const DEFAULT_POST_LOGIN_PATH = '/projects';

/**
 * Pages that work without a session. They live outside the guarded app route
 * in `addRouter.tsx` and are never used as a post-login redirect target.
 */
export const PUBLIC_PATHS = [
  '/login',
  '/activate',
  '/forgot',
  '/recover',
  '/confirm-email',
] as const;

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

/**
 * Validates a captured post-login redirect target.
 *
 * Only same-origin targets are accepted, so the login page cannot be used as an
 * open redirect. Prefix checks alone are not enough: the URL parser folds `\`
 * into `/` and strips tabs/newlines, so `/\evil.example` and `/<tab>/evil.example`
 * both resolve cross-origin despite starting with a single slash. Resolving
 * against the current origin and comparing is the only reliable test.
 *
 * Returns the normalized path (pathname + search + hash), or `undefined` when
 * the target is unusable — cross-origin, the root, or the login page itself
 * (redirecting there is a no-op, or a loop).
 */
export function sanitizeRedirect(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length === 0) return undefined;
  if (!value.startsWith('/')) return undefined;

  const origin = globalThis.location?.origin;
  if (!origin) return undefined;

  let resolved: URL;
  try {
    resolved = new URL(value, origin);
  } catch {
    return undefined;
  }
  if (resolved.origin !== origin) return undefined;

  const target = `${resolved.pathname}${resolved.search}${resolved.hash}`;
  if (target === '/' || isPublicPath(resolved.pathname)) return undefined;
  return target;
}
